import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { Banknote, CreditCard, Gift, HandCoins, QrCode, Trash2, Wallet } from 'lucide-react';
import { maxRedeemPaise, pointsFor } from '@shripos/shared/crm';
import { buildUpiUri } from '@shripos/shared/billing';
import { Alert, Button, Dialog, Input, cn } from '@shripos/ui';
import { rupees, toPaise, toRupeeInput } from '../../lib/money.js';

const MODES = [
  { key: 'cash', label: 'Cash', icon: Banknote },
  { key: 'upi', label: 'UPI', icon: QrCode },
  { key: 'card', label: 'Card', icon: CreditCard },
  { key: 'wallet', label: 'Wallet', icon: Wallet },
  { key: 'due', label: 'Credit', icon: HandCoins },
  { key: 'points', label: 'Points', icon: Gift },
];

/** Quick cash amounts: exact, then the next round notes above it. */
function cashSuggestions(amount) {
  const r = amount / 100;
  const out = new Set([r]);
  for (const step of [10, 50, 100, 500, 2000]) {
    const v = Math.ceil(r / step) * step;
    if (v > r) out.add(v);
  }
  return [...out].slice(0, 5);
}

function UpiQr({ upi, amountPaise, reference }) {
  const { t } = useTranslation();
  const [svg, setSvg] = useState(null);
  const uri = useMemo(() => {
    try {
      return upi?.vpa
        ? buildUpiUri({ vpa: upi.vpa, payeeName: upi.payeeName, amountPaise, note: reference, ref: reference })
        : null;
    } catch {
      return null;
    }
  }, [upi, amountPaise, reference]);
  useEffect(() => {
    if (!uri) return;
    QRCode.toString(uri, { type: 'svg', margin: 1 }).then(setSvg);
  }, [uri]);
  if (!upi?.vpa)
    return (
      <Alert tone="warning">
        {t('pos.upiMissing', "Add this outlet's UPI ID in Settings → This outlet to show a payment QR.")}
      </Alert>
    );
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 p-4">
      {svg && <div className="size-48 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: svg }} />}
      <p className="text-sm text-slate-600">
        <Trans
          i18nKey="pos.payTo"
          defaults="Pay <b>{{amount}}</b> to <code>{{vpa}}</code>"
          values={{ amount: rupees(amountPaise), vpa: upi.vpa }}
          components={{ b: <strong />, code: <span className="font-mono" /> }}
        />
      </p>
      <p className="text-xs text-slate-500">
        {t('pos.upiConfirm', 'Confirm the payment on your UPI app or soundbox, then press Received.')}
      </p>
    </div>
  );
}

/**
 * Collect one or more payments for `totalPaise`. Single-tender is one tap; split payments add
 * rows until the remaining amount is zero. Returns rows for the API (amount, tendered, reference).
 */
export function PaymentDialog({
  totalPaise,
  upi,
  hasCustomer,
  customer,
  loyalty,
  reference,
  busy,
  onComplete,
  onClose,
  offline = null,
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [mode, setMode] = useState('cash');
  const paidSoFar = rows.reduce((s, r) => s + r.amountPaise, 0);
  const remaining = totalPaise - paidSoFar;
  const [amountText, setAmountText] = useState(toRupeeInput(totalPaise));
  const [tenderedText, setTenderedText] = useState('');
  const [refText, setRefText] = useState('');
  const amount = Math.min(toPaise(amountText) ?? 0, remaining);
  const tendered = toPaise(tenderedText);
  // Loyalty points (Settings → Customers & reports): only with an attached customer.
  const pointsBalance =
    (customer?.points ?? 0) -
    rows.filter((r) => r.mode === 'points').reduce((s, r) => s + pointsFor(r.amountPaise, loyalty), 0);
  // Offline: no points (the balance can't be checked) and credit only when allowed in settings.
  const modes = MODES.filter(
    (m) =>
      (m.key !== 'points' || (loyalty && customer && !offline)) && (m.key !== 'due' || !offline || offline.allowCredit),
  );
  const maxPoints = loyalty && customer ? maxRedeemPaise(pointsBalance, remaining, loyalty) : 0;

  // After adding/removing a split row, prefill the next payment with what's left.
  const changeRows = (next) => {
    setRows(next);
    setAmountText(toRupeeInput(totalPaise - next.reduce((s, r) => s + r.amountPaise, 0)));
    setTenderedText('');
    setRefText('');
  };

  const entry = () => ({
    mode,
    amountPaise: amount,
    ...(mode === 'cash' && tendered ? { tenderedPaise: Math.max(tendered, amount) } : {}),
    ...(refText.trim() ? { reference: refText.trim() } : {}),
  });

  const entryError =
    amount <= 0
      ? t('pos.enterAmount', 'Enter an amount')
      : mode === 'cash' && tendered != null && tendered < amount
        ? t('pos.cashShort', 'Cash received is less than the amount')
        : mode === 'due' && !hasCustomer
          ? t('pos.creditNeedsCustomer', 'Attach a customer to bill on credit')
          : mode === 'points' && pointsBalance < (loyalty?.minRedeemPoints ?? 0)
            ? t('pos.pointsMinimum', 'Points can be used from {{min}} points (balance {{balance}})', {
                min: loyalty.minRedeemPoints,
                balance: pointsBalance,
              })
            : mode === 'points' && amount > maxPoints
              ? t('pos.pointsMax', 'Points cover at most {{amount}}', { amount: rupees(maxPoints) })
              : null;

  const finish = () => {
    if (totalPaise === 0) return onComplete([]);
    const all = remaining > 0 ? [...rows, entry()] : rows;
    onComplete(all);
  };
  const lastCovers = remaining > 0 && amount === remaining && !entryError;
  const change = mode === 'cash' && tendered != null && tendered > amount ? tendered - amount : 0;

  return (
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      title={`${t('pos.collect', 'Collect')} ${rupees(totalPaise)}`}
      size="md"
      footer={
        <div className="flex w-full items-center gap-2">
          {remaining > 0 && amount < remaining && (
            <Button variant="secondary" disabled={Boolean(entryError)} onClick={() => changeRows([...rows, entry()])}>
              {t('pos.addSplit', 'Add & split')}
            </Button>
          )}
          <Button
            size="lg"
            className="flex-1"
            disabled={busy || (totalPaise > 0 && remaining > 0 && !lastCovers)}
            loading={busy}
            onClick={finish}
            data-testid="complete-payment"
          >
            {mode === 'upi' && remaining > 0
              ? t('pos.received', 'Received · complete')
              : t('pos.complete', 'Complete sale')}
            {change > 0 ? ` · ${t('pos.changeShort', 'change {{amount}}', { amount: rupees(change) })}` : ''}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {rows.length > 0 && (
          <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
            {rows.map((r, i) => (
              <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="font-medium">
                  {t(`pos.mode.${r.mode}`, MODES.find((m) => m.key === r.mode)?.label)}
                </span>
                <span className="flex items-center gap-2">
                  <span className="tabular-nums">{rupees(r.amountPaise)}</span>
                  <button
                    onClick={() => changeRows(rows.filter((_, j) => j !== i))}
                    aria-label={t('pos.removePayment', 'Remove payment')}
                  >
                    <Trash2 className="size-4 text-slate-400 hover:text-red-600" />
                  </button>
                </span>
              </li>
            ))}
            <li className="flex justify-between px-3 py-2 text-sm font-semibold">
              <span>{t('pos.remaining', 'Remaining')}</span>
              <span className="tabular-nums">{rupees(remaining)}</span>
            </li>
          </ul>
        )}
        {totalPaise === 0 ? (
          <Alert tone="info">{t('pos.nothingToCollect', 'Nothing to collect — the bill total is ₹0.')}</Alert>
        ) : remaining > 0 ? (
          <>
            <div
              className={cn('grid gap-2', modes.length > 5 ? 'grid-cols-6' : 'grid-cols-5')}
              role="radiogroup"
              aria-label={t('pos.paymentMode', 'Payment mode')}
            >
              {modes.map((m) => (
                <button
                  key={m.key}
                  role="radio"
                  aria-checked={mode === m.key}
                  onClick={() => {
                    setMode(m.key);
                    if (m.key === 'points') setAmountText(toRupeeInput(Math.min(maxPoints, remaining)));
                    else if (mode === 'points') setAmountText(toRupeeInput(remaining));
                  }}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-xl py-3 text-xs font-semibold ring-1 transition',
                    mode === m.key
                      ? 'bg-slate-900 text-white ring-slate-900'
                      : 'text-slate-700 ring-slate-200 hover:bg-slate-50',
                  )}
                >
                  <m.icon className="size-5" />
                  {t(`pos.mode.${m.key}`, m.label)}
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">
                <span className="mb-1 block font-medium text-slate-700">{t('pos.amount', 'Amount (₹)')}</span>
                <Input
                  size="lg"
                  inputMode="decimal"
                  value={amountText}
                  onChange={(e) => setAmountText(e.target.value)}
                />
              </label>
              {mode === 'cash' ? (
                <label className="text-sm">
                  <span className="mb-1 block font-medium text-slate-700">
                    {t('pos.cashReceived', 'Cash received (₹)')}
                  </span>
                  <Input
                    size="lg"
                    inputMode="decimal"
                    autoFocus
                    value={tenderedText}
                    onChange={(e) => setTenderedText(e.target.value)}
                    placeholder={t('pos.optional', 'optional')}
                  />
                </label>
              ) : mode === 'points' ? (
                <div className="text-sm">
                  <span className="mb-1 block font-medium text-slate-700">{t('pos.mode.points', 'Points')}</span>
                  <p className="rounded-lg bg-brand-50 px-3 py-2.5 text-brand-900">
                    <Trans
                      i18nKey="pos.pointsUse"
                      defaults="Uses <b>{{points}}</b> of {{balance}} points"
                      values={{ points: pointsFor(amount, loyalty), balance: pointsBalance }}
                      components={{ b: <strong /> }}
                    />
                  </p>
                </div>
              ) : (
                mode !== 'due' && (
                  <label className="text-sm">
                    <span className="mb-1 block font-medium text-slate-700">{t('pos.reference', 'Reference')}</span>
                    <Input
                      size="lg"
                      value={refText}
                      onChange={(e) => setRefText(e.target.value.slice(0, 60))}
                      placeholder={
                        mode === 'upi' ? t('pos.utrOptional', 'UTR (optional)') : t('pos.optional', 'optional')
                      }
                    />
                  </label>
                )
              )}
            </div>
            {mode === 'cash' && (
              <div className="flex flex-wrap gap-2">
                {cashSuggestions(amount || remaining).map((v) => (
                  <button
                    key={v}
                    onClick={() => setTenderedText(String(v))}
                    className="rounded-lg px-3 py-2 text-sm font-semibold ring-1 ring-slate-300 hover:bg-slate-50"
                  >
                    ₹{v}
                  </button>
                ))}
              </div>
            )}
            {change > 0 && (
              <div className="rounded-xl bg-emerald-50 p-3 text-center text-emerald-900">
                {t('pos.returnChange', 'Return change')}{' '}
                <span className="text-2xl font-bold tabular-nums">{rupees(change)}</span>
              </div>
            )}
            {mode === 'upi' && <UpiQr upi={upi} amountPaise={amount} reference={reference} />}
            {entryError && amountText !== '' && <p className="text-sm text-red-600">{entryError}</p>}
          </>
        ) : (
          <Alert tone="success">{t('pos.fullyPaid', 'Fully paid. Complete the sale.')}</Alert>
        )}
      </div>
    </Dialog>
  );
}
